import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { errorColorFor } from '../shared/errorColor'

export type { ColorScheme }

const tokens = {
  dark: {
    bg: 'bg.input',
    text: 'text.primary',
    placeholder: 'text.search-placeholder',
    border: 'interactive.tertiary-border',
    focusRing: 'interactive.focus-ring',
  },
  light: {
    bg: 'light.bg.input',
    text: 'light.text.primary',
    placeholder: 'light.text.search-placeholder',
    border: 'light.interactive.tertiary-border',
    focusRing: 'light.interactive.focus-ring',
  },
} as const

export const multilineOuterStyles = (
  colorScheme: ColorScheme = 'dark',
  hasSideBorders = true
): SystemStyleObject => ({
  ...(hasSideBorders && {
    borderLeft: '1px solid',
    borderRight: '1px solid',
    borderColor:
      colorScheme === 'dark' ? 'border.subtle' : 'light.border.subtle',
  }),
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  p: '20px',
})

export const multilineLabelStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: colorScheme === 'dark' ? 'text.primary' : 'light.text.primary',
  fontFamily: "'Soehne Mono', sans-serif",
  fontSize: '8px',
  letterSpacing: '1px',
  lineHeight: '12px',
  pb: '4px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
})

export const actionButtonWrapperStyles = (
  visible: boolean
): SystemStyleObject => ({
  display: 'grid',
  gridTemplateRows: visible ? '1fr' : '0fr',
  transition: 'grid-template-rows 0.2s ease',
})

export const actionButtonInnerStyles: SystemStyleObject = {
  overflow: 'hidden',
}

export const multilineContainerStyles = (
  colorScheme: ColorScheme = 'dark',
  isFocused: boolean,
  width: string | number = '100%',
  hasWarning = false
): SystemStyleObject => ({
  bg: tokens[colorScheme].bg,
  border: '1px solid',
  borderColor: hasWarning
    ? errorColorFor(colorScheme)
    : isFocused
    ? tokens[colorScheme].focusRing
    : tokens[colorScheme].border,
  alignItems: 'flex-end',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  p: '16px',
  transition: 'border-color 0.15s ease',
  width,
})

export const multilineTextareaStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  bg: 'transparent',
  border: 'none',
  color: tokens[colorScheme].text,
  fontFamily: 'Soehne',
  fontSize: '16px',
  lineHeight: '16px',
  minH: '60px',
  outline: 'none',
  resize: 'none',
  w: 'full',
  _placeholder: { color: tokens[colorScheme].placeholder },
})

export const multilineWarningTextStyles = (
  colorScheme: ColorScheme
): SystemStyleObject => ({
  color: errorColorFor(colorScheme),
  fontSize: '8px',
  fontWeight: '600',
  letterSpacing: '0.08em',
  lineHeight: 1,
  pt: '4px',
  textTransform: 'uppercase',
})
